import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DialogConfig } from '../../../shared';
import { trackByValue } from '../../../shared/util';
import { BulkShippingLabelComponent } from '../../../shipping/components/bulk-shipping-label/bulk-shipping-label.component';
import {
  EPicklistPreviewSortId,
  IOrderSalesPicklistPreview,
  IOrderSalesPicklistPreviewItem,
} from '../../interfaces/order-picklist-preview';

@Component({
  selector: 'lum-order-posted-picklist-preview',
  templateUrl: './order-posted-picklist-preview.component.html',
  styleUrls: ['./order-posted-picklist-preview.component.scss'],
})
export class OrderPostedPicklistPreviewComponent implements OnChanges, OnDestroy {
  @Input()
  data :IOrderSalesPicklistPreview = {
    pickingItems: [],
    orders: [],
    warehouseAddress: null,
  };

  @Input() // Consolidate additional configs here
  config :{ canUpdateCheckedOrderStatus :boolean; checkedCount :number } = {
    canUpdateCheckedOrderStatus: false,
    checkedCount: 0,
  };

  @Input()
  loadingPicklistItems :boolean = false;

  @Input()
  compact :boolean = false;

  @Input()
  ftApplyPresets :boolean = false;

  @Output()
  adjustmentEntryEvent = new EventEmitter<number>();

  @Input()
  appIntegrationAccountId :number | null = null;

  @Output()
  quickFulfillmentFlowEvent = new EventEmitter<string>();

  @Output()
  closeEvent = new EventEmitter<boolean>();

  pickingItems :IOrderSalesPicklistPreviewItem[] = [];
  orderNumbers :string[] = [];
  orderIds :number[] = [];
  loading :boolean = false;
  orderNumberMaxDisplay :number = 4;
  trackByValue = trackByValue;
  private onDestroy$ = new Subject();
  picklistPreviewSortId = EPicklistPreviewSortId;

  constructor(
    private dialog :MatDialog,
    private router :Router,
  ) {}

  ngOnChanges({ data, loadingPicklistItems } :SimpleChanges) :void {
    this.pickingItems = this.data?.pickingItems ?? [];
    this.orderNumbers = [];

    if (this.data?.orders?.length > 0) {
      this.data.orders.forEach((order) => {
        this.orderNumbers.push(order.orderNumber);
      });
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onIncomingOrderChipClick(id :number = 0) :void {
    this.router.navigate(['orders', id]);
  }

  printShippingLabels() :void {
    this.dialog
      .open(BulkShippingLabelComponent, {
        data: {
          appIntegrationAccountId: this.appIntegrationAccountId,
          orders: this.data.orders,
          warehouseAddress: this.data.warehouseAddress ?? null,
          ftApplyPresets: this.ftApplyPresets,
        },
        width: DialogConfig.sizes['xxx-large'],
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        if (response) {
          this.closeEvent.emit(true);
        }
      });
  }

  sortData(sort :Sort) :void {
    if (!this.data?.pickingItems || !sort.active || sort.direction === '') {
      return;
    }

    const validSortProperties = Object.values(EPicklistPreviewSortId);
    if (!validSortProperties.includes(sort.active as EPicklistPreviewSortId)) {
      return;
    }

    const isAsc = sort.direction === 'asc';
    const active = sort.active as keyof IOrderSalesPicklistPreviewItem;

    this.pickingItems = [...this.data.pickingItems].sort((a, b) => {
      const previous = a[active] as string | number;
      const next = b[active] as string | number;

      return this.compare(previous, next, isAsc);
    });
  }

  compare(previous :number | string, next :number | string, isAsc :boolean) {
    const sortDirectionValue = isAsc ? 1 : -1;
    if (typeof previous === 'string' && typeof next === 'string') {
      return sortDirectionValue * previous.localeCompare(next);
    }

    if (typeof previous === 'number' && typeof next === 'number') {
      return sortDirectionValue * (previous - next);
    }

    return 0;
  }
}
